import type {
  DutyHandoffType,
  SmartTransferAssistantSettings,
  SmartTransferContextEngineering,
  SmartTransferFirstMessageMode,
  TransferDestination,
} from '@/features/tools/types'
import { formatPhoneForDisplay, normalizePhoneToE164 } from '@/utils/phone'

const DEFAULT_WARM_MODE =
  'warm-transfer-wait-for-operator-to-speak-first-and-then-say-summary'
const DEFAULT_WARM_TRANSCRIPT_PROMPT =
  'Here is the transcript:\n\n{{transcript}}\n\n'
const DEFAULT_WARM_INSTRUCTIONS =
  "Using the transcript, create a transfer statement along the lines of 'Hello, this is {#AI-Name} and I have {# Client Name} on the line who needs assistance with {#Reason for transfer}. I verified the following: - {bullet list of variables use to authenticate identity}... I will transfer them to you now.' + Include any special instructions or requests if requested. Keep the statement as brief as possible."

// The assistant-based warm transfer mode relay uses for Smart destinations.
// Its string contains "warm", so deriveHandoffType must check for this exact
// mode before falling back to the generic "warm" substring check.
export const SMART_TRANSFER_MODE = 'warm-transfer-experimental'
const DEFAULT_SMART_INSTRUCTIONS =
  "You are a call transfer assistant. Wait for the operator to answer, then ask if they're available to take the call. If the destination has a phone menu, use the dtmf tool to press one key at a time (e.g. key='2') and wait for each prompt to finish before pressing the next key. If they accept: call transferSuccessful. If you reach voicemail, get no answer, or cannot complete the menu: call transferCancel."
export const DEFAULT_SMART_FIRST_MESSAGE =
  'Hello, I have a patient on the line. Are you available to take this call?'
const DEFAULT_SMART_FIRST_MESSAGE_MODE: SmartTransferFirstMessageMode =
  'assistant-speaks-first'
// Mirrors relay's hardcoded defaults (see SmartTransferAssistantSettings in
// relay/utils/models/duties.py) so exposing these fields doesn't silently
// change existing behavior for destinations that haven't set them yet.
const DEFAULT_SMART_MAX_DURATION_SECONDS = 60
const DEFAULT_SMART_SILENCE_TIMEOUT_SECONDS = 30
const DEFAULT_SMART_CONTEXT_ENGINEERING: SmartTransferContextEngineering = 'all'

// Relay's field constraints (SmartTransferAssistantSettings in
// relay/utils/models/duties.py: max_duration_seconds ge=10 le=43200,
// silence_timeout_seconds ge=5 le=3600). Named and shared with DestinationForm
// so the UI's input bounds/clamping can't silently drift from relay's
// contract -- a mismatch there degrades silently (relay's ValidationError
// handler swaps the whole smartAssistant block for defaults).
export const SMART_MAX_DURATION_SECONDS_MIN = 10
export const SMART_MAX_DURATION_SECONDS_MAX = 43200
export const SMART_SILENCE_TIMEOUT_SECONDS_MIN = 5
export const SMART_SILENCE_TIMEOUT_SECONDS_MAX = 3600

export type DraftDestination = {
  id: string
  number: string
  extension: string
  message: string
  description: string
  instructions: string
  // Smart-only fields below; harmless/unused for cold/warm destinations.
  firstMessage: string
  firstMessageMode: SmartTransferFirstMessageMode
  maxDurationSeconds: number
  silenceTimeoutSeconds: number
  fallbackMessage: string
  fallbackEndCallEnabled: boolean
  contextEngineering: SmartTransferContextEngineering
  // Per-destination handoff type (warm/cold/smart), derived from this destination's own transferPlan.
  handoffType: DutyHandoffType
  // The exact bytes this destination will be persisted as, not the bytes it was read as.
  raw: TransferDestination
}

const asString = (v: unknown) =>
  typeof v === 'string' && v.trim().length > 0 ? v : ''

const asNumber = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback

const asBoolean = (v: unknown, fallback: boolean): boolean =>
  typeof v === 'boolean' ? v : fallback

// `allowed` is a Record (not an array) so TypeScript forces every member of
// T to be listed -- a value outside the union (older config, manual API
// edit) falls back instead of reaching relay, where an invalid enum fails
// SmartTransferAssistantSettings validation and degrades the *whole* block
// (including authored instructions) to defaults.
const asEnum = <T extends string>(
  v: unknown,
  allowed: Record<T, true>,
  fallback: T
): T => (typeof v === 'string' && (v as T) in allowed ? (v as T) : fallback)

const FIRST_MESSAGE_MODE_VALUES: Record<SmartTransferFirstMessageMode, true> = {
  'assistant-speaks-first': true,
  'assistant-waits-for-user': true,
  'assistant-speaks-first-with-model-generated-message': true,
}

const CONTEXT_ENGINEERING_VALUES: Record<
  SmartTransferContextEngineering,
  true
> = {
  all: true,
  none: true,
}

/** Reads `dest.smartAssistant` as a partial settings object, or undefined
 * when absent/malformed. Every reader below defaults missing fields itself. */
function readSmartAssistant(
  dest: Record<string, unknown>
): Partial<SmartTransferAssistantSettings> | undefined {
  const smartAssistant = dest.smartAssistant
  return smartAssistant && typeof smartAssistant === 'object'
    ? (smartAssistant as Partial<SmartTransferAssistantSettings>)
    : undefined
}

export function extractInstructions(dest: Record<string, unknown>): string {
  const transferPlan =
    dest.transferPlan && typeof dest.transferPlan === 'object'
      ? (dest.transferPlan as Record<string, unknown>)
      : null

  // Smart destinations store their prompt in the nested smartAssistant
  // object — there is no summaryPlan to read it from.
  if (transferPlan?.mode === SMART_TRANSFER_MODE) {
    return asString(readSmartAssistant(dest)?.instructions)
  }

  const summaryPlan =
    transferPlan?.summaryPlan && typeof transferPlan.summaryPlan === 'object'
      ? (transferPlan.summaryPlan as Record<string, unknown>)
      : null
  const messages =
    Array.isArray(summaryPlan?.messages) && summaryPlan?.messages
      ? (summaryPlan.messages as unknown[])
      : []

  for (const m of messages) {
    if (!m || typeof m !== 'object') continue
    const msg = m as Record<string, unknown>
    if (msg.role !== 'system') continue
    const content = asString(msg.content)
    if (content) return content
  }

  const legacySummary = asString(dest.summary)
  if (legacySummary) return legacySummary

  return ''
}

// Smart iff mode is exactly the assistant-based experimental mode; warm iff
// mode contains "warm" (checked after smart, since the smart mode string
// also contains "warm"); a blind-transfer plan reads cold. This read must
// stay in sync with the mode strings toApiDestination writes.
export function deriveHandoffType(dest: TransferDestination): DutyHandoffType {
  const mode = dest.transferPlan?.mode
  if (mode === SMART_TRANSFER_MODE) return 'smart'
  return typeof mode === 'string' && mode.toLowerCase().includes('warm')
    ? 'warm'
    : 'cold'
}

export function toDraftDestination(
  dest: TransferDestination
): DraftDestination {
  const smartAssistant = readSmartAssistant(dest)
  return {
    id: crypto.randomUUID(),
    number: formatPhoneForDisplay(asString(dest.number)),
    extension: asString(dest.extension),
    message: asString(dest.message),
    description: asString(dest.description),
    instructions: extractInstructions(dest),
    firstMessage: asString(smartAssistant?.firstMessage),
    firstMessageMode: asEnum(
      smartAssistant?.firstMessageMode,
      FIRST_MESSAGE_MODE_VALUES,
      DEFAULT_SMART_FIRST_MESSAGE_MODE
    ),
    maxDurationSeconds: asNumber(
      smartAssistant?.maxDurationSeconds,
      DEFAULT_SMART_MAX_DURATION_SECONDS
    ),
    silenceTimeoutSeconds: asNumber(
      smartAssistant?.silenceTimeoutSeconds,
      DEFAULT_SMART_SILENCE_TIMEOUT_SECONDS
    ),
    fallbackMessage: asString(smartAssistant?.fallbackMessage),
    fallbackEndCallEnabled: asBoolean(
      smartAssistant?.fallbackEndCallEnabled,
      true
    ),
    contextEngineering: asEnum(
      smartAssistant?.contextEngineering,
      CONTEXT_ENGINEERING_VALUES,
      DEFAULT_SMART_CONTEXT_ENGINEERING
    ),
    handoffType: deriveHandoffType(dest),
    raw: dest,
  }
}

// Shared with DestinationForm so the "does this type get a default prompt"
// check can't drift out of sync between the seed guard, the visibility
// check, and the read-side default fallback below.
export const hasPromptDefault = (handoffType: DutyHandoffType): boolean =>
  handoffType === 'warm' || handoffType === 'smart'

export function defaultInstructionsFor(handoffType: DutyHandoffType): string {
  if (handoffType === 'warm') return DEFAULT_WARM_INSTRUCTIONS
  if (handoffType === 'smart') return DEFAULT_SMART_INSTRUCTIONS
  return ''
}

export function emptyDraftDestination({
  handoffType = 'cold',
}: {
  handoffType?: DutyHandoffType
} = {}): DraftDestination {
  return {
    id: crypto.randomUUID(),
    number: '',
    extension: '',
    message: '',
    description: '',
    instructions: defaultInstructionsFor(handoffType),
    firstMessage: handoffType === 'smart' ? DEFAULT_SMART_FIRST_MESSAGE : '',
    firstMessageMode: DEFAULT_SMART_FIRST_MESSAGE_MODE,
    maxDurationSeconds: DEFAULT_SMART_MAX_DURATION_SECONDS,
    silenceTimeoutSeconds: DEFAULT_SMART_SILENCE_TIMEOUT_SECONDS,
    fallbackMessage: '',
    fallbackEndCallEnabled: true,
    contextEngineering: DEFAULT_SMART_CONTEXT_ENGINEERING,
    handoffType,
    raw: {},
  }
}

export function toApiDestination(draft: DraftDestination): TransferDestination {
  // Merged, not rebuilt, so VAPI fields hoku doesn't model survive an edit.
  const out: TransferDestination = { ...draft.raw }

  // Numbers are stored display-formatted; persist them as E164.
  out.number = normalizePhoneToE164(draft.number) ?? draft.number

  // The backend stamps type only when absent, so a wrong stored one would be permanent.
  out.type = 'number'

  // Deleted rather than skipped: against a merge base, skipping keeps the stored value.
  for (const key of ['extension', 'description', 'message'] as const) {
    const value = draft[key].trim()
    if (value) out[key] = value
    else delete out[key]
  }

  // Read by extractInstructions, but VAPI v3 rejects it as a root property.
  delete out.summary

  // A non-object stored plan reads cold, so the merge would republish it unchanged.
  const plan: unknown = out.transferPlan
  if (!plan || typeof plan !== 'object' || Array.isArray(plan)) {
    delete out.transferPlan
  }

  if (draft.handoffType === 'warm') {
    // Replaced whole: spreading the stored plan would resurrect other modes' keys.
    out.transferPlan = {
      mode: DEFAULT_WARM_MODE,
      sipVerb: 'refer',
      summaryPlan: {
        enabled: true,
        messages: [
          { role: 'system', content: draft.instructions.trim() },
          { role: 'user', content: DEFAULT_WARM_TRANSCRIPT_PROMPT },
        ],
      },
    }
    delete out.smartAssistant
  } else if (draft.handoffType === 'smart') {
    // Relay owns building the actual transfer assistant (model, tools,
    // fallbackPlan, contextEngineeringPlan) from this mode marker; Hoku
    // supplies the marker plus the nested settings object, which relay
    // validates (SmartTransferAssistantSettings) and never forwards to VAPI
    // as-is.
    out.transferPlan = { mode: SMART_TRANSFER_MODE, sipVerb: 'dial' }
    const smartAssistant: SmartTransferAssistantSettings = {
      instructions: draft.instructions.trim(),
      firstMessage: draft.firstMessage.trim(),
      firstMessageMode: draft.firstMessageMode,
      maxDurationSeconds: draft.maxDurationSeconds,
      silenceTimeoutSeconds: draft.silenceTimeoutSeconds,
      fallbackMessage: draft.fallbackMessage.trim(),
      fallbackEndCallEnabled: draft.fallbackEndCallEnabled,
      contextEngineering: draft.contextEngineering,
    }
    out.smartAssistant = smartAssistant
  } else if (deriveHandoffType(draft.raw) !== 'cold') {
    // Guarded, not unconditional: an unguarded delete would destroy a stored blind plan.
    delete out.transferPlan
    delete out.smartAssistant
  }

  return out
}
